import React, { useRef } from 'react';
import styled from 'styled-components';
import { format } from 'date-fns';

import Image from './Image';
import BlogPostContent from './BlogPostContent';

import { BlogPostContentFragment } from '../schemas/api';

import SubscribeSection from './SubscribeSection';

import BREAKPOINTS from '../styles/breakpoints';
import { Grid, columns } from '../styles/grid';
import COLORS from '../styles/colors';
import TYPO from '../styles/typography';
import MarkdownStyle from '../styles/markdown';

import Button from '../styles/button';
import Icon from './Icon';
import ShareSection from './ShareSection';
import useTypeformScript from '../hooks/useTypeformScript';

const Container = styled(Grid)`
  padding: 0 0 60px;
  ${BREAKPOINTS.max.medium`
    padding: 72px 24px 36px;
  `}
`;

const Header = styled.div`
  margin: 60px 0 12px;

  ${BREAKPOINTS.max.medium`
  margin: 24px 0;
  `}

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 18px;
`;

const Content = styled.div`
  ${columns(5, 6)};
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${BREAKPOINTS.max.medium`
  ${columns(4, 9)};
  `}
  ${BREAKPOINTS.max.small`
  ${columns(1, 12)};
  `}
`;

const PreviewImage = styled.div`
  ${columns(2, 3)};

  ${BREAKPOINTS.max.medium`
  ${columns(1, 3)};
  padding: 12px 24px;
  `}

  ${BREAKPOINTS.max.small`
  ${columns(2, 10)};
  `}

  max-width:240px;
  margin: 0 auto;
  padding: 48px 24px;

  & > span {
    box-shadow: 6px 6px 8px rgba(0, 0, 0, 0.2);
    border-radius: 6px;
    border: 1px solid white !important;
  }
  & img {
    width: 100%;
    height: auto;
    margin: 0 auto;
  }

  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
`;

const Heading = styled.div`
  ${TYPO.h1};
`;

const Description = styled.div`
  padding: 0;
  font-size: 16px;
  ${MarkdownStyle}

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 60px auto 12px;
    padding-bottom: 12px;
  }

  p,
  blockquote,
  ul,
  ol,
  dl,
  table,
  pre {
    margin: 20px 0 0;
    line-height: 180%;
  }

  ${BREAKPOINTS.max.small`
    font-size: 14px;
    & > figure {
      margin: 24px auto;
    }
  `}
`;

const DateContainer = styled.div`
  display: flex;
  gap: 6px;
  color: ${COLORS.shades.s300.css};
  ${TYPO.subheading}
`;

const Author = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  ${TYPO.p2};
  font-weight: 600;
`;

type PictureProps = { $src: string };

const Picture = styled.div<PictureProps>`
  background-image: ${(props) => `url('${props.$src}')`};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-size: cover;
`;
const Footer = styled.div`
  padding: 160px 12px 0;
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: 72px;
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 36px;
  width: 100%;
  padding: 72px 0;
`;

type BlogPostProps = {
  data: BlogPostContentFragment;
};

export default function BlogPost({ data }: BlogPostProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  useTypeformScript();

  return (
    <Container>
      {data.image && (
        <PreviewImage>
          <Image
            src={data.image.url}
            width={data.image.width}
            height={data.image.height}
            alt={data.title}
          />
          {data.typeformFormId && (
            <Button
              onClick={() => {
                if (ref.current) {
                  ref.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                  });
                }
              }}
              $variant="secondary"
            >
              <Icon name="Download" width={12} height={12} />
              Download now
            </Button>
          )}
        </PreviewImage>
      )}
      <Content>
        <Header>
          <Title>
            <DateContainer>
              {format(
                new Date(data.publicationDate || data.sys.publishedAt),
                'MMM dd, yyyy'
              )}
            </DateContainer>
            <Heading>{data.title}</Heading>
          </Title>
          {data.author && (
            <Author>
              <Picture $src={data.author.picture.url} />
              <span>{data.author.name}</span>
            </Author>
          )}
        </Header>
        <Description>
          <BlogPostContent>{data.content}</BlogPostContent>
          {data.contentTwo && (
            <BlogPostContent>{data.contentTwo}</BlogPostContent>
          )}
        </Description>
        {data.typeformFormId && (
          <FormSection ref={ref}>
            <div data-tf-live={data.typeformFormId}></div>{' '}
          </FormSection>
        )}

        <Footer>
          <SubscribeSection />
          <ShareSection />
        </Footer>
      </Content>
    </Container>
  );
}
